'use client';

import React, { useState } from 'react';
import styled, { css } from 'styled-components';

export type PanelTitleVariant = 'body' | 'eyebrow';

export interface PanelProps extends Omit<React.HTMLAttributes<HTMLElement>, 'title'> {
  /** Title text rendered in the panel header. Omit for headerless panels. */
  title?: React.ReactNode;
  /** Heading element used for the title. Defaults to `h2`. */
  titleAs?: 'h2' | 'h3' | 'h4';
  /**
   * Visual treatment for the title:
   * - `body` (default): body-sized semibold, on a tinted header strip — for KPI groups, drawer sections.
   * - `eyebrow`: uppercase letter-spaced, no header strip — for record-detail multi-panel bodies.
   */
  titleVariant?: PanelTitleVariant;
  /** Right-aligned slot in the header row (typically a button or toolbar). */
  actions?: React.ReactNode;
  /**
   * When true, the header acts as a toggle button and the body can be hidden.
   * Use only for long-tail panels (Audit trail, Shared profiles). Never collapse
   * primary content (Trip, Charges, Passenger, Notes).
   */
  collapsible?: boolean;
  /** Initial open state when `collapsible`. Defaults to `true`. */
  defaultOpen?: boolean;
  /**
   * Optional summary slot rendered next to the collapse chevron — typically a
   * count or short status (e.g. `"5 events"`). Only rendered when `collapsible`.
   */
  summary?: React.ReactNode;
  children?: React.ReactNode;
}

const Surface = styled.section<{ $eyebrow: boolean }>`
  background: ${(p) => p.theme.colors.neutral.panel};
  border: 1px solid ${(p) => p.theme.colors.neutral.line};
  border-radius: ${(p) => p.theme.radius.card};
  overflow: hidden;
  ${(p) =>
    p.$eyebrow &&
    css`
      padding: ${p.theme.spaceAlias.lg};
      display: flex;
      flex-direction: column;
      gap: ${p.theme.spaceAlias.md};
    `}
`;

const headerBase = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${(p) => p.theme.spaceAlias.sm};
`;

const Header = styled.header<{ $eyebrow: boolean }>`
  ${headerBase}
  ${(p) =>
    !p.$eyebrow &&
    css`
      padding: ${p.theme.space[3]} ${p.theme.space[4]};
      border-bottom: 1px solid ${p.theme.colors.neutral.line};
      background: ${p.theme.colors.neutral.panelAlt};
    `}
`;

const HeaderButton = styled.button<{ $eyebrow: boolean }>`
  ${headerBase}
  background: transparent;
  border: 0;
  padding: 0;
  margin: 0;
  width: 100%;
  cursor: pointer;
  color: inherit;
  text-align: left;
  font: inherit;

  &:focus-visible {
    outline: 2px solid ${(p) => p.theme.colors.brand.primary};
    outline-offset: 2px;
    border-radius: ${(p) => p.theme.radius.sm};
  }

  ${(p) =>
    !p.$eyebrow &&
    css`
      padding: ${p.theme.space[3]} ${p.theme.space[4]};
      border-bottom: 1px solid ${p.theme.colors.neutral.line};
      background: ${p.theme.colors.neutral.panelAlt};
    `}
`;

const TitleBody = styled.h2`
  margin: 0;
  font-family: ${(p) => p.theme.fontFamily.ui};
  font-size: ${(p) => p.theme.fontSize.body};
  font-weight: ${(p) => p.theme.fontWeight.semibold};
  letter-spacing: -0.1px;
  color: ${(p) => p.theme.colors.neutral.ink};
`;

const TitleEyebrow = styled.h2`
  margin: 0;
  font-family: ${(p) => p.theme.fontFamily.ui};
  font-size: ${(p) => p.theme.fontSize.xs};
  font-weight: ${(p) => p.theme.fontWeight.bold};
  color: ${(p) => p.theme.colors.neutral.inkMute};
  letter-spacing: 0.8px;
  text-transform: uppercase;
`;

const Body = styled.div<{ $eyebrow: boolean }>`
  ${(p) =>
    !p.$eyebrow &&
    css`
      padding: ${p.theme.space[4]};
    `}
`;

const HeaderRight = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${(p) => p.theme.spaceAlias.sm};
  color: ${(p) => p.theme.colors.neutral.inkSoft};
`;

const Chevron = styled.span<{ $open: boolean }>`
  display: inline-flex;
  transform: rotate(${(p) => (p.$open ? 180 : 0)}deg);
  transition: transform 0.15s ease;
  color: ${(p) => p.theme.colors.neutral.inkSoft};
`;

/**
 * Sectioned surface primitive: a {@link Card} with an opinionated header row
 * for `title` and `actions`. Supports two title variants:
 *
 * - `body` (default): body-sized title on a tinted header strip — for KPI
 *   groups, drawer sections, dialog bodies.
 * - `eyebrow`: uppercase eyebrow style — for record-detail multi-panel bodies
 *   per `liftango-ui-ops/references/layout.mdx`.
 *
 * Set `collapsible` to make the header act as a toggle. Use this only for
 * long-tail panels (audit trail, shared profiles); never for primary content.
 *
 * For unstructured surfaces use `Card` directly.
 */
export function Panel({
  title,
  titleAs = 'h2',
  titleVariant = 'body',
  actions,
  collapsible = false,
  defaultOpen = true,
  summary,
  children,
  ...rest
}: PanelProps) {
  const [open, setOpen] = useState(defaultOpen);
  const isEyebrow = titleVariant === 'eyebrow';
  const TitleEl = isEyebrow ? TitleEyebrow : TitleBody;

  const hasHeader = title !== undefined || actions !== undefined || collapsible;

  const headerInner = (
    <>
      {title !== undefined && <TitleEl as={titleAs}>{title}</TitleEl>}
      <HeaderRight>
        {collapsible && summary !== undefined && <span>{summary}</span>}
        {!collapsible && actions !== undefined && <div>{actions}</div>}
        {collapsible && (
          <Chevron $open={open} aria-hidden="true">
            <ChevronGlyph />
          </Chevron>
        )}
      </HeaderRight>
    </>
  );

  return (
    <Surface $eyebrow={isEyebrow} {...rest}>
      {hasHeader &&
        (collapsible ? (
          <HeaderButton
            type="button"
            $eyebrow={isEyebrow}
            aria-expanded={open}
            onClick={() => setOpen((o) => !o)}
          >
            {headerInner}
          </HeaderButton>
        ) : (
          <Header $eyebrow={isEyebrow}>{headerInner}</Header>
        ))}
      {(!collapsible || open) && <Body $eyebrow={isEyebrow}>{children}</Body>}
    </Surface>
  );
}

function ChevronGlyph() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3 9l4-4 4 4" />
    </svg>
  );
}
